import { useState } from "react";
import { Plus, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Modal, inp, lbl } from "../gear/GearBits";
import TripCoverSuggestions from "./TripCoverSuggestions";

/* Edit the trip's core details — title, description, dates and destination stops. */
export const TripEditDialog = ({ trip, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: trip.title || "", description: trip.description || "",
    start_date: trip.start_date || "", end_date: trip.end_date || "",
    stops: trip.stops || [],
  });
  const [newStop, setNewStop] = useState("");
  const [busy, setBusy] = useState(false);
  // Cover picked from Unsplash — attached only if trip currently has no cover.
  const [newCoverFile, setNewCoverFile] = useState(null);
  const [newCoverPreview, setNewCoverPreview] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addStop = () => {
    const s = newStop.trim();
    if (!s) return;
    set("stops", [...form.stops, s]);
    setNewStop("");
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("End date is before the start date — check the dates");
      return;
    }
    setBusy(true);
    try {
      await api.post("/trips", {
        id: trip.id, title: form.title.trim(), description: form.description.trim() || null,
        start_date: form.start_date || null, end_date: form.end_date || null, stops: form.stops,
      });
      // If member picked an Unsplash cover AND the trip has no cover yet, attach it.
      if (newCoverFile && !trip.cover) {
        try {
          const fd = new FormData();
          fd.append("file", newCoverFile);
          fd.append("owner_type", "trip");
          fd.append("owner_id", trip.id);
          fd.append("category", "photo");
          fd.append("label", "Cover photo");
          const { data: att } = await api.post("/trips/attachments", fd,
            { headers: { "Content-Type": "multipart/form-data" } });
          await api.post("/trips", { id: trip.id, title: form.title.trim() || trip.title, cover: att.id });
        } catch { /* silent — save already succeeded */ }
      }
      toast.success("Trip updated");
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the trip"); }
    setBusy(false);
  };

  return (
    <Modal title="Edit trip" onClose={onClose}>
      <div className="space-y-4" data-testid="trip-edit-dialog">
        <div>
          <label className={lbl}>Trip title *</label>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={120}
            data-testid="trip-edit-title" className={inp} placeholder="e.g. Delhi–Jaipur 2026" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Start date</label>
            <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)}
              data-testid="trip-edit-start" className={inp} />
          </div>
          <div>
            <label className={lbl}>End date</label>
            <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)}
              data-testid="trip-edit-end" className={inp} min={form.start_date || undefined} />
          </div>
        </div>
        {form.start_date && form.end_date && form.end_date < form.start_date && (
          <p className="text-xs text-rose-600 font-600" role="alert" aria-live="polite" data-testid="trip-edit-date-warning">
            End date is before the start date.
          </p>
        )}
        <div>
          <label className={lbl}>Destinations / stops</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {form.stops.map((s, i) => (
              <span key={`${s}-${i}`} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 text-sm px-2 py-1" data-testid={`trip-edit-stop-${i}`}>
                {s}
                <button onClick={() => set("stops", form.stops.filter((_, j) => j !== i))}
                  data-testid={`trip-edit-stop-remove-${i}`} className="text-blue-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
            {form.stops.length === 0 && <span className="text-xs text-muted-foreground">No stops yet — add the places you're visiting, in order.</span>}
          </div>
          <div className="flex gap-2">
            <input value={newStop} onChange={(e) => setNewStop(e.target.value)} maxLength={80}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStop(); } }}
              data-testid="trip-edit-stop-input" className={inp} placeholder="Add a stop (e.g. Jaipur)" />
            <button onClick={addStop} data-testid="trip-edit-stop-add"
              className="shrink-0 inline-flex items-center gap-1 border border-zinc-300 px-3 text-sm font-600 hover:border-primary"><Plus className="w-4 h-4" /> Add</button>
          </div>
        </div>
        <div>
          <label className={lbl}>Description</label>
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} maxLength={1000}
            data-testid="trip-edit-description" className={inp} placeholder="Notes about this trip…" />
        </div>
        {!trip.cover && (
          <div data-testid="trip-edit-cover-picker">
            <label className={lbl}>Cover photo <span className="font-400 text-muted-foreground">(optional — this trip has no cover yet)</span></label>
            <div className="flex items-center gap-3">
              {newCoverPreview ? (
                <div className="relative">
                  <img src={newCoverPreview} alt="cover preview" data-testid="trip-edit-cover-preview"
                    className="w-16 h-16 rounded object-cover border border-zinc-300" />
                  <button type="button" onClick={() => { setNewCoverFile(null); if (newCoverPreview) URL.revokeObjectURL(newCoverPreview); setNewCoverPreview(null); }}
                    data-testid="trip-edit-cover-clear"
                    className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 rounded border border-dashed border-zinc-400 bg-white grid place-items-center">
                  <ImageIcon className="w-5 h-5 text-zinc-400" />
                </div>
              )}
              <span className="text-xs text-muted-foreground">
                {newCoverFile ? "Cover ready — will attach after you save" : "Tap a suggestion below to back-fill this trip's cover"}
              </span>
            </div>
            <TripCoverSuggestions
              query={form.title || trip.title}
              onPick={(f) => {
                setNewCoverFile(f);
                try { setNewCoverPreview(URL.createObjectURL(f)); } catch { setNewCoverPreview(null); }
              }}
              testid="trip-edit-cover-suggestion"
            />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm" data-testid="trip-edit-cancel">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="trip-edit-save"
            className="px-5 py-2 bg-primary text-white text-sm font-700 hover:bg-blue-700 disabled:opacity-50">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
};
